<script lang="ts" setup>
import { ref, reactive, watch } from 'vue'
import { showNotify, showLoadingToast, closeToast } from 'vant'
import { format, parse } from 'date-fns'
import { useTranslation } from 'i18next-vue'
import { useAppStore } from '@/stores/AppStore'

// Composables
import { useAppointmentForm } from '@/composables/Appointment/useAppointmentForm'
import { useAppointmentCalendar } from '@/composables/Appointment/useAppointmentCalendar'
import { useAppointmentSelections } from '@/composables/Appointment/useAppointmentSelections'
import { useAppointmentRepository } from '@/repositories/appointmentRepo'
import { useAppointmentActions } from '@/composables/Appointment/useAppointmentActions'
import { AppointmentBranch } from '@/type/Appointment'
import { getBranchDisplayNamei18n, type BranchNameFields } from '@/utils/branch'

// Components
import AppointmentFormFields from './AppointmentFormFields.vue'
import AppointmentBranchDoctorSelector from './AppointmentBranchDoctorSelector.vue'
import AppointmentTimeSlotSelector from './AppointmentTimeSlotSelector.vue'
import AppointmentErrorDialogs from './AppointmentErrorDialogs.vue'
import AppointmentNoteFields from '@/components/Appointment/AppointmentNoteFields.vue'

interface Props {
  show: boolean
  appointmentUuid: string
}
const props = defineProps<Props>()

interface Emits {
  (e: 'close'): void
  (e: 'success'): void
}
const emit = defineEmits<Emits>()

const { t } = useTranslation()
const appStore = useAppStore()
const appointmentRepo = useAppointmentRepository()
const appointmentActions = useAppointmentActions()

const loading = ref(false)
const branches = ref<AppointmentBranch[]>([])
const settings = ref<any>({})
const noAppointments = ref<any[]>([])
const limitDialog = ref(false)
const postPoneDialog = ref(false)
const today = new Date()

const oldAppointment = reactive({
  branch: '',
  date: '2020-01-01',
  time: [] as any[],
})
const oldAppointmentBranch = ref<BranchNameFields>({})

function updateOldAppointmentBranchDisplay() {
  oldAppointment.branch = getBranchDisplayNamei18n(oldAppointmentBranch.value, appStore.language)
}

// Use composables
const {
  form,
  disableState,
  mobileValidator,
  emailValidator,
  populateFormFromPatient,
  updateNameByLanguage,
  resetFormKeepPatientData,
} = useAppointmentForm()
const {
  showCalendar,
  dateSelect,
  dateText,
  calendarMinDate,
  calendarMaxDate,
  clinicWorkHour,
  workHourDensityColorLabel,
  setWorkHourDensityColorLabel,
  selectedTime,
  selectedTimeIndex,
  loadingWorkHour,
  formatDate,
  setCalendarDate,
  mappingWorkingHourSlot,
  onTimeSelect,
  formatter: calendarFormatter,
  clearTimeSelection,
  clearDateSelection,
} = useAppointmentCalendar()
const {
  branchText,
  showBranchPicker,
  doctorText,
  showDoctorPicker,
  doctors,
  doctorCalendars,
  doctorCalendarsIn,
  onConfirmBranch: baseOnConfirmBranch,
  onConfirmDoctor: baseOnConfirmDoctor,
  clearDoctorSelection,
  clearBranchSelection,
  updateBranchTextByLanguage,
} = useAppointmentSelections()

function clearSelect(options: { doctor?: boolean; date?: boolean; time?: boolean }) {
  if (options.doctor) {
    clearDoctorSelection()
  }
  if (options.date) {
    clearDateSelection()
  }
  if (options.time) {
    clearTimeSelection()
  }
}

async function initialize() {
  if (!appStore.channelId || !appStore.clinicUUID || !appStore.userProfile.userId || !props.appointmentUuid) {
    showNotify({ type: 'danger', message: t('unknown_error') })
    onClose()
    return
  }

  try {
    loading.value = true
    const [patient, branchList, settingData] = await Promise.all([
      appointmentRepo.fetchAppointmentData({
        clinicId: appStore.clinicUUID,
        lineUserId: appStore.userProfile.userId,
        channelId: appStore.channelId,
        appointmentUUID: props.appointmentUuid,
      }),
      appointmentRepo.fetchBranches({
        clinicId: appStore.clinicUUID,
        lineUserId: appStore.userProfile.userId,
        channelId: appStore.channelId,
      }),
      appointmentRepo.fetchAppointmentSettings({
        clinicId: appStore.clinicUUID,
        lineUserId: appStore.userProfile.userId,
        channelId: appStore.channelId,
      }),
    ])

    // Set fetched data
    settings.value = settingData.data
    branches.value = branchList || []
    setCalendarDate(settings.value)

    // Check limits
    if (patient.is_limit) {
      limitDialog.value = true
    }
    if (patient.can_postpone === false) {
      postPoneDialog.value = true
    }

    // Populate form from patient data
    if (patient.data) {
      populateFormFromPatient(patient.data, appStore.language)

      // Set old appointment info
      if (patient.data.appointment) {
        oldAppointmentBranch.value = patient.data.appointment
        updateOldAppointmentBranchDisplay()
        const parsedDate = parse(patient.data.appointment.date, 'dd/MM/yyyy', new Date())
        oldAppointment.date = format(parsedDate, 'dd MMM yyyy')
        oldAppointment.time = patient.data.appointment.time
      }
    }

    // Auto select when only have one branch
    if (branchList.length === 1 && !form.branch && !branchText.value) {
      onConfirmBranch({
        text: getBranchDisplayNamei18n(branches.value[0], appStore.language),
        uuid: branchList[0].uuid,
      })
    }
  } catch (err: unknown) {
    showNotify({
      type: 'danger',
      message: t('unknown_error'),
    })
  } finally {
    loading.value = false
  }
}

async function fetchNoAppointment() {
  if (!form.branch || !appStore.channelId) return
  noAppointments.value = await appointmentRepo.fetchNoAppointmentDates({
    branchUuid: form.branch,
    channelId: appStore.channelId,
  })
}

async function onConfirmBranch({ text, uuid }: { text: string; uuid: string }) {
  form.branch = uuid
  await baseOnConfirmBranch(
    { text, uuid },
    {
      clearSelect,
      fetchNoAppointment,
      fetchDoctors: async (branchUuid: string) => {
        return await appointmentRepo.fetchDoctors({
          clinicId: appStore.clinicUUID,
          lineUserId: appStore.userProfile.userId,
          branchUuid,
        })
      },
      onConfirmDoctor,
    },
    t,
  )
}

async function onConfirmDoctor({ selectedOptions }: { selectedOptions: any[] }) {
  form.doctor = selectedOptions[0].uuid
  await baseOnConfirmDoctor(
    { selectedOptions },
    {
      clearSelect,
      fetchDoctorSchedule: async (doctorUuid: string) => {
        return await appointmentRepo.fetchDoctorWorkSchedule({
          clinicId: appStore.clinicUUID,
          lineUserId: appStore.userProfile.userId,
          branchUuid: form.branch!,
          doctorUuid,
        })
      },
    },
    settings.value,
  )
}

async function onConfirmCalendar(date: Date) {
  form.date = format(date, 'yyyy-MM-dd')
  showCalendar.value = false
  dateText.value = formatDate(date)
  dateSelect.value = date
  loadingWorkHour.value = true

  const data = await appointmentRepo.fetchTimeSlots({
    clinicId: appStore.clinicUUID,
    branchUuid: form.branch!,
    doctorUuid: form.doctor,
    date: form.date,
  })

  setWorkHourDensityColorLabel(data.density_description)

  clinicWorkHour.value = mappingWorkingHourSlot(
    data.working_hour_json_slot,
    date,
    noAppointments.value,
    settings.value,
    doctorCalendars.value,
    form.doctor,
    form.date,
  )
  loadingWorkHour.value = false
}

function formatterWrapper(day: any) {
  return calendarFormatter(day, noAppointments.value, settings.value, doctorCalendarsIn.value, form.doctor, today, t)
}

function clearAllSelection() {
  clearSelect({ doctor: true, date: true, time: true })
  clearBranchSelection()
  resetFormKeepPatientData()
}

async function onSubmit() {
  if (selectedTime.value.length === 0) {
    return showNotify({
      type: 'danger',
      message: t('appointmentrequest.please_select_time'),
    })
  }

  loading.value = true
  const result = await appointmentActions.postponeAppointment({
    appointmentUuid: props.appointmentUuid,
    clinicId: appStore.clinicUUID,
    lineUserId: appStore.userProfile.userId,
    channelId: appStore.channelId!,
    data: {
      name: form.name,
      mobile: form.mobile,
      email: form.email,
      branch: form.branch,
      doctor: form.doctor,
      date: form.date,
      time: selectedTime.value,
      // เลื่อนนัดคงบริการ / หัตถการของนัดเดิม (backend ไม่อ่าน) - ไม่ส่งบริการ ส่ง note ตามที่พิมพ์
      note: form.note,
      interest_tags: [],
    },
  })
  loading.value = false

  if (result.success) {
    emit('success')
    onClose()
  } else if (result.isLimit) {
    limitDialog.value = true
  }
}

const onClose = () => {
  emit('close')
  clearAllSelection()
}

watch(
  () => props.show,
  async val => {
    if (val) {
      await initialize()
    }
  },
)

watch(showCalendar, val => {
  if (val) clearTimeSelection()
})

watch(
  () => loading.value,
  loading => {
    if (loading) {
      showLoadingToast({
        forbidClick: true,
        duration: 0,
        loadingType: 'spinner',
      })
    } else {
      closeToast()
    }
  },
)

watch(
  () => appStore.language,
  () => {
    updateNameByLanguage(appStore.language)
    updateBranchTextByLanguage(branches.value, form.branch, appStore.language)
    updateOldAppointmentBranchDisplay()
  },
)
</script>

<template>
  <van-popup :show="show" position="bottom" :style="{ height: '100%' }">
    <div class="tw-flex tw-flex-col tw-h-full">
      <!-- Header -->
      <div class="tw-flex tw-justify-between tw-items-center tw-p-4 tw-border-b">
        <h2 class="tw-text-xl tw-font-bold">{{ t('appointmentrequest.postpone_title') }}</h2>
        <van-icon name="cross" class="tw-cursor-pointer tw-text-2xl" @click="onClose" />
      </div>

      <!-- Content -->
      <div class="tw-flex-1 tw-overflow-y-auto">
        <van-form @submit="onSubmit">
          <div class="tw-mx-4">
            <!-- Old Appointment Info -->
            <van-cell-group class="group-container" inset>
              <van-field readonly :label="t('appointmentrequest.old_appointment')">
                <template #input> {{ oldAppointment.date }} ({{ oldAppointment.time }}) </template>
              </van-field>
              <van-field readonly :label="t('appointmentrequest.branch')">
                <template #input> {{ oldAppointment.branch }} </template>
              </van-field>
            </van-cell-group>
            <van-divider>{{ t('appointmentrequest.appointment_info') }}</van-divider>

            <!-- Form Fields Component -->
            <AppointmentFormFields
              :name="form.name"
              :mobile="form.mobile"
              :email="form.email"
              :disable-name="disableState.disableName"
              :disable-mobile="disableState.disableMobile"
              :disable-email="disableState.disableEmail"
              :mobile-validator="mobileValidator"
              :email-validator="emailValidator"
              @update:name="form.name = $event"
              @update:mobile="form.mobile = $event"
              @update:email="form.email = $event"
            />

            <!-- Branch/Doctor/Date Selector Component -->
            <AppointmentBranchDoctorSelector
              :branch-text="branchText"
              :show-branch-picker="showBranchPicker"
              :branches="branches"
              :doctor-text="doctorText"
              :show-doctor-picker="showDoctorPicker"
              :doctors="doctors"
              :hiding-doctor="settings.appoint_liff_hiding_doctor"
              :has-branch="!!form.branch"
              :date-text="dateText"
              :show-calendar="showCalendar"
              :calendar-min-date="calendarMinDate"
              :calendar-max-date="calendarMaxDate"
              :date-select="dateSelect"
              :formatter="formatterWrapper"
              :current-language="appStore.language"
              @update:show-branch-picker="showBranchPicker = $event"
              @update:show-doctor-picker="showDoctorPicker = $event"
              @update:show-calendar="showCalendar = $event"
              @confirm-branch="onConfirmBranch"
              @confirm-doctor="onConfirmDoctor"
              @confirm-calendar="onConfirmCalendar"
            />

            <!-- Time Slot Selector Component -->
            <AppointmentTimeSlotSelector
              :has-date="!!form.date"
              :loading="loadingWorkHour"
              :time-slots="clinicWorkHour"
              :selected-time-index="selectedTimeIndex"
              :density-color-label="workHourDensityColorLabel"
              @select-time="onTimeSelect"
            />

            <!-- เลื่อนนัดไม่มีช่องเลือกบริการ / หัตถการ - backend คงของนัดเดิมไว้ (LMP-7809, LMP-7840) -->

            <!-- Note Field -->
            <AppointmentNoteFields v-model="form.note" :helper-text="settings.appoint_liff_helper_msg" />
          </div>

          <!-- Submit Section -->
          <div class="tw-pt-5 tw-pb-3">
            <van-notice-bar
              class="tw-mt-4"
              wrapable
              :scrollable="false"
              :text="`${t('appointmentrequest.note')}: ${settings.appoint_liff_warning_msg || ''}`"
              left-icon="volume-o"
            />
            <div class="tw-m-4">
              <van-button round block type="primary" native-type="submit" class="tw-text-white">
                {{ t('appointmentrequest.submit_postpone') }}
              </van-button>
            </div>
          </div>
        </van-form>
      </div>

      <!-- Dialogs Component -->
      <AppointmentErrorDialogs
        :show-limit="limitDialog"
        :show-postpone="postPoneDialog"
        @close-limit="limitDialog = false"
        @close-postpone="postPoneDialog = false"
      />
    </div>
  </van-popup>
</template>

<style scoped>
.group-container {
  margin: 15px 0px;
  border: 2px solid #c8c9cc;
  border-radius: 20px;
}
</style>
